import * as React from "react";
import { cn } from "@/lib/utils";

type Variant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "caption"
  | "overline";

const variantStyles: Record<Variant, string> = {
  h1: "text-[clamp(22px,5vw,61px)] font-bold leading-tight text-black",
  h2: "text-[clamp(20px,4vw,52px)] font-semibold leading-tight text-black",
  h3: "text-[clamp(18px,3vw,30px)] font-semibold leading-snug",
  h4: "text-[clamp(16px,2.5vw,24px)] font-semibold leading-snug",
  h5: "text-[clamp(14px,2vw,24px)] font-semibold leading-snug",
  h6: "text-[clamp(13px,1.8vw,22px)] font-medium leading-snug",

  subtitle1: "text-[clamp(14px,1.5vw,16px)] font-medium leading-normal",
  subtitle2: "text-[clamp(14px,1.3vw,14px)] font-medium leading-normal",

  body1: "text-[clamp(14px,1.6vw,18px)] font-medium leading-normal",
  body2: "text-[clamp(14px,1.8vw,20px)] font-medium leading-normal",

  caption: "text-[clamp(14px,1.5vw,16px)] font-normal leading-normal",
  overline: "text-[clamp(14px,1.5vw,16px)] font-medium tracking-widest leading-normal",
};

const defaultTag: Record<Variant, keyof React.JSX.IntrinsicElements> = {
  h1:        "h1",
  h2:        "h2",
  h3:        "h3",
  h4:        "h4",
  h5:        "h5",
  h6:        "h6",
  subtitle1: "p",
  subtitle2: "p",
  body1:     "p",
  body2:     "p",
  caption:   "span",
  overline:  "span",
};

const colorStyles: Record<string, string> = {
  default:     "text-foreground",
  muted:       "text-muted-foreground",
  primary:     "text-primary",
  destructive: "text-destructive",
  success:     "text-success",
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: Variant;
  as?: keyof React.JSX.IntrinsicElements;
  color?: "default" | "muted" | "primary" | "destructive" | "success";
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ variant = "body1", as, color = "default", className, children, ...props }, ref) => {
    const Tag = (as ?? defaultTag[variant]) as React.ElementType;
    return (
      <Tag
        ref={ref}
        className={cn(variantStyles[variant], colorStyles[color], className)}
        {...props}
      >
        {children}
      </Tag>
    );
  }
);

Typography.displayName = "Typography";

export { Typography };
